import { useState, useEffect, useRef, useCallback } from 'react';

const LS_PREFIX = 'cb.view.';
const LS_ACTIVE = 'cb.view-active.';
const LS_VIEWS_LIST = 'cb.views-list.';

export interface NamedView<T> {
  id: string;
  name: string;
  value: T;
  isDefault: boolean;
}

export interface SavedViewsAPI<T> {
  /** The current view's value — set this to mutate the active view. */
  value: T;
  setValue: (next: T | ((prev: T) => T)) => void;
  /** All views the user has saved for this key. */
  views: NamedView<T>[];
  /** The currently active view (may be `null` while the list is still loading). */
  activeView: NamedView<T> | null;
  /** Save the current value under a new name (or rename in place if name exists). */
  saveAs: (name: string) => Promise<void>;
  /** Switch the active view to another by id. */
  switchTo: (id: string) => void;
  /** Rename a view. */
  rename: (id: string, name: string) => Promise<void>;
  /** Delete a view. If the active view is deleted, falls back to the default or first remaining. */
  remove: (id: string) => Promise<void>;
  /** Mark a view as the default. */
  setDefault: (id: string) => Promise<void>;
  /** True while initial hydration from server is in flight. */
  hydrating: boolean;
}

/**
 * Merge a stored view value over `initial` so every top-level key the consumer
 * expects is present. A persisted view can drift from the current shape — a
 * schema change here, a legacy row, or a value written under the same key by
 * something else — and a missing key surfacing as `undefined` will crash the
 * consumer (the Dashboard read `view.sort.dir` off exactly such a value).
 * Non-object values are returned untouched.
 */
function reconcileViewValue<T>(stored: T, initial: T): T {
  const isObj = (x: unknown): x is Record<string, unknown> =>
    typeof x === 'object' && x !== null && !Array.isArray(x);
  if (isObj(stored) && isObj(initial)) return { ...initial, ...stored } as T;
  return stored;
}

/**
 * Single-value compatibility shim: callers that just want one persisted view
 * still get `[value, setValue]` like the old hook. New callers use
 * `useNamedViews` directly for the full menu API.
 */
export function useSavedView<T>(key: string, initial: T): [T, (next: T | ((prev: T) => T)) => void] {
  const api = useNamedViews<T>(key, initial);
  return [api.value, api.setValue];
}

/**
 * Multi-view manager. Persists every named view to localStorage and (if a
 * session exists) to /api/views. The active-view id is also persisted so
 * tab refreshes restore the user's last selection.
 */
export function useNamedViews<T>(key: string, initial: T): SavedViewsAPI<T> {
  // Active-view id (localStorage primary, hydrated from server on mount)
  const [activeId, setActiveId] = useState<string | null>(() => {
    try { return localStorage.getItem(LS_ACTIVE + key); } catch { return null; }
  });

  // Views list — start from localStorage cache for instant first render
  const [views, setViews] = useState<NamedView<T>[]>(() => {
    try {
      const raw = localStorage.getItem(LS_VIEWS_LIST + key);
      if (raw) return JSON.parse(raw) as NamedView<T>[];
    } catch { /* ignore */ }
    return [];
  });

  const [hydrating, setHydrating] = useState(true);

  // Current value — backed by the active view, or `initial` if no views yet
  const activeView = views.find(v => v.id === activeId) ?? null;
  const value = activeView ? reconcileViewValue(activeView.value, initial) : initial;

  // Mirror views list to localStorage on every change
  useEffect(() => {
    try { localStorage.setItem(LS_VIEWS_LIST + key, JSON.stringify(views)); } catch { /* full */ }
  }, [key, views]);
  useEffect(() => {
    try {
      if (activeId) localStorage.setItem(LS_ACTIVE + key, activeId);
      else localStorage.removeItem(LS_ACTIVE + key);
    } catch { /* full */ }
  }, [key, activeId]);

  // Mirror the active view's value to the legacy LS_PREFIX slot so older
  // useSavedView consumers reading the same key still see the right thing.
  useEffect(() => {
    try {
      if (activeView) localStorage.setItem(LS_PREFIX + key, JSON.stringify(activeView.value));
    } catch { /* full */ }
  }, [key, activeView]);

  // Hydrate from server on mount
  const hydrated = useRef(false);
  useEffect(() => {
    if (hydrated.current) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`/api/views?viewKey=${encodeURIComponent(key)}`, { credentials: 'include' });
        if (!res.ok) { setHydrating(false); return; }
        const json = await res.json();
        const rows: NamedView<T>[] = (json.data ?? []).map((r: { id: string; name: string; value: T; isDefault: boolean }) => ({
          id: r.id, name: r.name, value: r.value, isDefault: r.isDefault,
        }));
        if (!cancelled) {
          setViews(rows);
          if (rows.length > 0) {
            const pickId = activeId && rows.some(r => r.id === activeId) ? activeId
              : (rows.find(r => r.isDefault)?.id ?? rows[0].id);
            setActiveId(pickId);
          }
          hydrated.current = true;
        }
      } catch { /* offline — local cache stays authoritative */ }
      if (!cancelled) setHydrating(false);
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  // setValue mutates the active view; if none exists, create a "Default" one
  const pushTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const setValue = useCallback((next: T | ((prev: T) => T)) => {
    setViews(prev => {
      const id = activeId;
      let list = prev;
      const apply = (v: T) => typeof next === 'function' ? (next as (p: T) => T)(v) : next;
      let updatedView: NamedView<T> | null = null;
      if (id && list.some(v => v.id === id)) {
        list = list.map(v => v.id === id ? (updatedView = { ...v, value: apply(v.value) }) : v);
      } else {
        const newId = `local-${Date.now()}`;
        const v: NamedView<T> = { id: newId, name: 'Default', value: apply(initial), isDefault: true };
        list = [...list, v];
        updatedView = v;
        setActiveId(newId);
      }
      // Debounced server push
      if (pushTimer.current) clearTimeout(pushTimer.current);
      const captured = updatedView!;
      pushTimer.current = setTimeout(async () => {
        try {
          const r = await fetch('/api/views', {
            method: 'POST',
            credentials: 'include',
            headers: { 'content-type': 'application/json' },
            body: JSON.stringify({
              viewKey: key,
              name: captured.name,
              value: captured.value,
              isDefault: captured.isDefault,
            }),
          });
          if (r.ok) {
            const json = await r.json();
            // Server returns canonical id — adopt it
            if (json.data?.id && captured.id !== json.data.id) {
              setViews(curr => curr.map(v => v.id === captured.id ? { ...v, id: json.data.id } : v));
              if (activeId === captured.id) setActiveId(json.data.id);
            }
          }
        } catch { /* keep local */ }
      }, 600);
      return list;
    });
  }, [activeId, initial, key]);

  const saveAs = useCallback(async (name: string) => {
    const newView: NamedView<T> = {
      id: `local-${Date.now()}`,
      name,
      value,
      isDefault: views.length === 0,
    };
    setViews(prev => [...prev, newView]);
    setActiveId(newView.id);
    try {
      const r = await fetch('/api/views', {
        method: 'POST', credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ viewKey: key, name, value, isDefault: newView.isDefault }),
      });
      if (r.ok) {
        const json = await r.json();
        if (json.data?.id) {
          setViews(prev => prev.map(v => v.id === newView.id ? { ...v, id: json.data.id } : v));
          setActiveId(json.data.id);
        }
      }
    } catch { /* ignore */ }
  }, [value, views.length, key]);

  const switchTo = useCallback((id: string) => { setActiveId(id); }, []);

  const rename = useCallback(async (id: string, name: string) => {
    const target = views.find(v => v.id === id);
    if (!target) return;
    setViews(prev => prev.map(v => v.id === id ? { ...v, name } : v));
    try {
      await fetch('/api/views', {
        method: 'POST', credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ viewKey: key, name, value: target.value, isDefault: target.isDefault }),
      });
    } catch { /* ignore */ }
  }, [views, key]);

  const remove = useCallback(async (id: string) => {
    setViews(prev => prev.filter(v => v.id !== id));
    if (activeId === id) {
      const remaining = views.filter(v => v.id !== id);
      setActiveId(remaining.find(v => v.isDefault)?.id ?? remaining[0]?.id ?? null);
    }
    try { await fetch(`/api/views/${id}`, { method: 'DELETE', credentials: 'include' }); }
    catch { /* ignore */ }
  }, [activeId, views]);

  const setDefault = useCallback(async (id: string) => {
    setViews(prev => prev.map(v => ({ ...v, isDefault: v.id === id })));
    try { await fetch(`/api/views/${id}/default`, { method: 'POST', credentials: 'include' }); }
    catch { /* ignore */ }
  }, []);

  return {
    value, setValue,
    views, activeView,
    saveAs, switchTo, rename, remove, setDefault,
    hydrating,
  };
}
